import { useState, type ReactNode } from "react";
import { SparklesIcon } from "lucide-react";

import { Popper } from "@/components/atoms/Popper";
import { Mutations } from "@/constants";
import { useBackendMutation } from "@/hooks/useBackendMutation";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { Typography } from "@/components/ui/typography";

/**
 * Say what you want, read it back, keep it or ask again.
 *
 * Every part of this app that is assembled out of menus can be described in a
 * sentence far faster than it can be built - and a sentence is only worth
 * offering if what comes back can be checked before it takes effect. So this
 * owns the whole of that exchange: the button, the box to say it in, the
 * waiting, and the row of Cancel / Ask again / Use it. What it never owns is
 * the preview - ``preview`` is drawn by whatever the draft is of, in that
 * thing's own words, because a preview in other words is a preview of
 * something else.
 *
 * The server side is the same shape: a kind of thing declares what a draft of
 * it looks like (``app.core.services.drafting``), and both ends then have one
 * path rather than one per feature.
 */
export function AiRewrite<T>({
  kind,
  current,
  placeholder,
  preview,
  onApply,
  label = "Describe this",
}: {
  /** What is being written, as the server registers it: ``report_widget``. */
  kind: string;
  /** What the reader is looking at, so "make it a pie" is a change to it. */
  current?: unknown;
  placeholder?: string;
  /** The draft, drawn by whatever holds it; ``change`` keeps an edit to it. */
  preview: (draft: T, change: (next: T) => void) => ReactNode;
  onApply: (draft: T) => void;
  /** What the button says to a reader who cannot see it. */
  label?: string;
}) {
  const isOpenState = useState(false);
  const [isOpen, setIsOpen] = isOpenState;
  const [said, setSaid] = useState("");
  const [draft, setDraft] = useState<T | null>(null);

  const { mutate, loading } = useBackendMutation({
    mutation: Mutations.DRAFT_FROM_PROMPT,
    callbacks: {
      onSuccess: (data: unknown) => {
        const answered = (data as { draft?: T } | null)?.draft;
        if (answered == null) return;
        setDraft(answered);
      },
    },
  });

  const ask = () => {
    if (!said.trim()) return;
    mutate({
      variables: {
        input: { kind, prompt: said.trim(), current: current ?? null },
      },
    });
  };

  const close = () => {
    setIsOpen(false);
    setDraft(null);
  };

  return (
    <Popper
      isOpenState={isOpenState}
      placement="bottom-end"
      autoFocus={false}
      anchor={
        <Button
          variant="outline"
          size="icon-sm"
          aria-label={label}
          data-testid="ai-rewrite"
          onPointerDown={(event) => event.stopPropagation()}
          onClick={(event) => {
            event.stopPropagation();
            setIsOpen(!isOpen);
          }}
          className="shrink-0"
        >
          <SparklesIcon size={14} />
        </Button>
      }
    >
      <div
        data-testid="ai-rewrite-prompt"
        className="p-3 w-105 flex flex-col gap-2"
        onPointerDown={(event) => event.stopPropagation()}
      >
        {loading || draft != null ? (
          <>
            <Typography level="body-xs" className="text-muted-foreground/80">
              This would be
            </Typography>
            <div className="rounded-sm bg-muted p-2">
              {loading ? (
                // The shape of the answer while it is being written, where the
                // answer itself will stand, so nothing jumps when it arrives.
                <div
                  data-testid="ai-rewrite-waiting"
                  className="flex flex-col gap-1"
                >
                  <Skeleton
                    variant="text"
                    level="title-md"
                    className="w-[80%]"
                  />
                  <Skeleton
                    variant="text"
                    level="title-md"
                    className="w-[55%]"
                  />
                </div>
              ) : (
                preview(draft as T, setDraft)
              )}
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={close}>
                Cancel
              </Button>
              <Button
                variant="ghost"
                size="sm"
                disabled={loading}
                onClick={() => setDraft(null)}
              >
                Ask again
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={loading}
                onClick={() => {
                  onApply(draft as T);
                  setSaid("");
                  close();
                }}
              >
                Use it
              </Button>
            </div>
          </>
        ) : (
          <>
            <Textarea
              autoFocus
              className="min-h-[3lh] max-h-[8lh] text-xs"
              placeholder={placeholder}
              value={said}
              onChange={(event) => setSaid(event.target.value)}
              onKeyDown={(event) => {
                // Written over several lines, sent with the modifier - a plain
                // Return is a new line, which is what a box this tall is for.
                if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
                  event.preventDefault();
                  ask();
                }
              }}
            />
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={close}>
                Cancel
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={ask}
                disabled={!said.trim()}
              >
                Ask
              </Button>
            </div>
          </>
        )}
      </div>
    </Popper>
  );
}
